import { useEffect, useRef } from "preact/hooks";

/**
 * 로그인 카드.
 *
 * 3D 는 두 층이다 — 카드가 포인터를 따라 기울고(반사광이 함께 움직인다), 그 3D 공간 안에
 * 아이소메트릭 큐브가 떠 있다. 회전 애니메이션을 상시로 돌리지 않는 게 의도다: 그건
 * 그 자체로 촌스럽고, 3면짜리 큐브를 한 바퀴 돌리면 없는 면(안쪽)이 보인다.
 */

/** 기울기 각도는 작게 (±7도). 크면 텍스트 가독성이 떨어지고 장난스러워진다. */
const MAX_TILT_X = 14;
const MAX_TILT_Y = 10;

function useTilt<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    // 움직임을 원치 않는 사용자에게는 아무 것도 하지 않는다 (CSS 도 같이 끈다).
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let frame = 0;
    const onMove = (ev: PointerEvent): void => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const r = node.getBoundingClientRect();
        const px = (ev.clientX - r.left) / r.width;
        const py = (ev.clientY - r.top) / r.height;
        node.classList.add("live");
        // CSS 변수만 건드린다 — CSSOM 직접 조작은 CSP 의 inline-style 규칙과 무관하다.
        node.style.setProperty("--rx", `${((px - 0.5) * MAX_TILT_X).toFixed(2)}deg`);
        node.style.setProperty("--ry", `${((0.5 - py) * MAX_TILT_Y).toFixed(2)}deg`);
        node.style.setProperty("--mx", `${(px * 100).toFixed(1)}%`);
        node.style.setProperty("--my", `${(py * 100).toFixed(1)}%`);
      });
    };
    const onLeave = (): void => {
      node.classList.remove("live");
      node.style.setProperty("--rx", "0deg");
      node.style.setProperty("--ry", "0deg");
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerleave", onLeave);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerleave", onLeave);
    };
  }, []);
  return ref;
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#4285F4" d="M45 24c0-1.6-.1-2.7-.4-4H24v8h12c-.2 2-1.5 5-4.7 7l7.3 5.6C42.9 36.3 45 30.7 45 24z" />
      <path fill="#34A853" d="M24 46c6 0 11-2 14.6-5.4l-7.3-5.6c-2 1.4-4.6 2.3-7.3 2.3-5.7 0-10.5-3.8-12.2-9l-7.5 5.8C7.9 41.2 15.3 46 24 46z" />
      <path fill="#FBBC05" d="M11.8 28.3A13.4 13.4 0 0 1 11 24c0-1.5.3-3 .7-4.3L4.2 13.9A22 22 0 0 0 2 24c0 3.6.9 6.9 2.3 9.9l7.5-5.6z" />
      <path fill="#EA4335" d="M24 10.2c3.2 0 6.1 1.1 8.4 3.3l6.4-6.4C34.9 3.6 30 1.5 24 1.5 15.3 1.5 7.9 6.3 4.2 13.9l7.5 5.8C13.5 14.2 18.3 10.2 24 10.2z" />
    </svg>
  );
}

/**
 * 큐브: front/right/top 3면만 만든다.
 *
 * 두 값이 눈으로 확정한 것이다 —
 * `rotateY` 는 **음수**여야 오른쪽 면이 앞으로 온다(양수면 만들지 않은 왼쪽 면이 와서
 * L 자로 보인다), 그리고 면의 `border-radius` 는 작아야 한다(12px 이면 인접한 두 면의
 * 둥근 모서리가 만나지 못해 모서리마다 틈이 벌어진다).
 */
function Cube() {
  return (
    <div class="cube" aria-hidden="true">
      <div class="face front" />
      <div class="face right" />
      <div class="face top" />
    </div>
  );
}

export function Login({ ssoEnabled, error }: { ssoEnabled: boolean; error?: string }) {
  const card = useTilt<HTMLDivElement>();
  return (
    <div class="gate">
      <div class="aura" aria-hidden="true">
        <i />
        <i />
      </div>
      <div class="card" ref={card}>
        <Cube />
        <h1>timely-sandbox</h1>
        <p class="sub">운영 콘솔 · 조회 전용</p>
        {ssoEnabled ? (
          <a class="gbtn" href="/auth/login">
            <GoogleMark />
            회사 Google 계정으로 로그인
          </a>
        ) : (
          <p class="note">
            이 배포에는 로그인이 구성되지 않았습니다.
            <br />
            로컬에서 <code>tsbx dashboard</code> 를 쓰세요.
          </p>
        )}
        <p class="note">허용된 운영자만 들어옵니다. 변경 작업은 admin 키가 따로 필요합니다.</p>
        {error ? <p class="err-line">{error}</p> : null}
      </div>
    </div>
  );
}
